import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefundApprovalItem, RefundApprovalService } from './refund-approval.service';

type RefundApprovalRow = Omit<RefundApprovalItem, 'amount' | 'booking'> & {
  amount: number;
  booking: Omit<RefundApprovalItem['booking'], 'totalAmount'> & { totalAmount: number };
};

@Component({
  selector: 'app-refund-approval-page',
  imports: [
    DatePipe,
    DecimalPipe,
    LucideAngularModule,
    InputTextModule,
    TableModule,
    RefreshButtonComponent,
    StatusBadge,
  ],
  templateUrl: './refund-approval-page.component.html',
})
export class RefundApprovalPageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly service = inject(RefundApprovalService);

  readonly rows = signal<RefundApprovalRow[]>([]);
  readonly selected = signal<RefundApprovalRow | null>(null);
  readonly isLoadingRefunds = signal(false);

  ngOnInit(): void {
    void this.loadRefunds();
  }

  async loadRefunds(): Promise<void> {
    if (this.isLoadingRefunds()) return;
    this.isLoadingRefunds.set(true);
    this.overlay.showLoading('กำลังโหลดรายการคืนเงินรออนุมัติ...');
    try {
      const items = await this.service.getPending();
      this.rows.set(
        items.map((item) => ({
          ...item,
          amount: Number(item.amount),
          booking: { ...item.booking, totalAmount: Number(item.booking.totalAmount) },
        })),
      );
    } catch (error: unknown) {
      this.showError('โหลดรายการคืนเงินไม่สำเร็จ', error);
    } finally {
      this.isLoadingRefunds.set(false);
      this.overlay.hideLoading();
    }
  }

  open(row: RefundApprovalRow): void {
    this.selected.set(row);
  }

  close(): void {
    this.selected.set(null);
  }

  openAttachment(path: string | null): void {
    if (path) window.open(this.service.resolveFileUrl(path), '_blank', 'noopener,noreferrer');
  }

  async approve(row: RefundApprovalRow): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'อนุมัติการคืนเงิน',
      message: `ยืนยันอนุมัติ ${row.refundNo} จำนวน ${row.amount.toLocaleString()} บาท`,
      confirmText: 'อนุมัติ',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังอนุมัติรายการคืนเงิน...');
    try {
      await this.service.approve(row.id);
      this.remove(row.id);
      this.overlay.success('อนุมัติสำเร็จ', `${row.refundNo} ถูกส่งให้ฝ่ายบัญชีดำเนินการจ่ายแล้ว`);
    } catch (error: unknown) {
      this.showError('อนุมัติไม่สำเร็จ', error);
    } finally {
      this.overlay.hideLoading();
    }
  }

  async reject(row: RefundApprovalRow): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'ไม่อนุมัติการคืนเงิน',
      message: `ยืนยันไม่อนุมัติ ${row.refundNo} หรือไม่`,
      confirmText: 'ไม่อนุมัติ',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังบันทึกผลการอนุมัติ...');
    try {
      await this.service.reject(row.id);
      this.remove(row.id);
      this.overlay.success('บันทึกผลแล้ว', `${row.refundNo} ไม่ผ่านการอนุมัติ`);
    } catch (error: unknown) {
      this.showError('บันทึกผลไม่สำเร็จ', error);
    } finally {
      this.overlay.hideLoading();
    }
  }

  private remove(id: string): void {
    this.rows.update((rows) => rows.filter((item) => item.id !== id));
    this.close();
  }

  private showError(title: string, error: unknown): void {
    const message =
      error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
        ? error.error.message
        : 'กรุณาลองใหม่อีกครั้ง';
    this.overlay.error(title, message);
  }
}
